/**
 * §5.4 — the standing indicator, shown on every screen of an active session.
 *
 * docs/ux-mockup.html puts it in the phone's footer: the running total on the
 * left, the player's place in the room on the right, as a chip that changes
 * weight with the standing — podium, top ten, or plain.
 */

import type { EventPayload } from "@tq/protocol";

export interface StandingBarProps {
  standing: EventPayload<"player.standing.updated">;
}

export function StandingBar({ standing }: StandingBarProps): React.ReactElement {
  return (
    <div className="tq-phone-footer">
      <span className="tq-phone-footer-stat">
        <span className="tq-phone-footer-label">Score</span>
        <b>{standing.totalScore.toLocaleString()}</b>
      </span>
      {/*
        The place is labelled, and it is labelled on the phone rather than only
        implied by a medal. "1,200 · 12 of 40" left players reading the second
        number as a second score; the word in front of it is what makes it a
        standing. The chip beside it carries the same fact in the form the room
        cares about — a medal, a streak, or a plain place.
      */}
      <span className="tq-phone-footer-stat tq-phone-footer-stat--place">
        <span className="tq-phone-footer-label">Place</span>
        <RankChip position={standing.position} participants={standing.participants} />
      </span>
    </div>
  );
}

export function RankChip({
  position,
  participants,
}: {
  position: number | null;
  participants: number;
}): React.ReactElement {
  // §29.2 — never color alone: the chip always carries its own text.
  if (position === null) {
    return (
      <span className="tq-phone-rank tq-phone-rank--unranked">— of {participants || "—"}</span>
    );
  }
  if (position <= 3) {
    const medal = position === 1 ? "🥇" : position === 2 ? "🥈" : "🥉";
    return (
      <span className="tq-phone-rank tq-phone-rank--podium">
        {medal} {ordinal(position)}
      </span>
    );
  }
  if (position <= 10) {
    return (
      <span className="tq-phone-rank tq-phone-rank--top10">
        🔥 {ordinal(position)} of {participants}
      </span>
    );
  }
  // An ordinal here too, not a bare number. "12 of 40" reads as a count of
  // something; "12th of 40" is the one thing a player outside the top ten
  // actually wants off this screen — where they are, and how far there is to go.
  return (
    <span className="tq-phone-rank">
      {ordinal(position)} of {participants}
    </span>
  );
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
